import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    // Capture phase so this runs before the cart icon's listener: the product page's own add-to-cart dispatches
    // 'cart:update' without data.itemCount, which would otherwise zero the cart bubble.
    document.addEventListener(CartAddEvent.eventName, this.#normaliseCartEvent, true);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('click', this.#handleKitClick);
    this.addEventListener('change', this.#handleKitChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#normaliseCartEvent, true);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('click', this.#handleKitClick);
    this.removeEventListener('change', this.#handleKitChange);
  }

  /**
   * Fills in data.itemCount from the cart resource when a custom add-to-cart omitted it.
   * @param {Event} event
   */
  #normaliseCartEvent = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!detail || !detail.data || detail.data.itemCount != null) return;
    const count = detail.resource?.item_count;
    if (typeof count === 'number') detail.data.itemCount = count;
  };

  #handleKitChange = (event) => {
    const select = event.target instanceof Element ? event.target.closest('select[data-cart-kit-select]') : null;
    if (!(select instanceof HTMLSelectElement)) return;
    const price = select.selectedOptions[0]?.dataset.price;
    const priceEl = select.closest('[data-cart-kit]')?.querySelector('[data-cart-kit-price]');
    if (price && priceEl) priceEl.textContent = price;
  };

  #handleKitClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-cart-kit-add]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const select = button.closest('[data-cart-kit]')?.querySelector('[data-cart-kit-select]');
    const id = Number(select?.value);
    if (!id) return;

    const label = button.textContent;
    button.disabled = true;
    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
      });
      if (!res.ok) throw new Error(`cart/add ${res.status}`);
      const cart = await (await fetch(`${root}cart.js`)).json();
      document.dispatchEvent(
        new CustomEvent(CartAddEvent.eventName, {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'cart-kit-suggestion',
            data: { source: 'cart-kit-suggestion', itemCount: cart.item_count },
          },
        })
      );
    } catch (error) {
      console.error('[cart-drawer] kit add failed', error);
      button.disabled = false;
      button.textContent = label;
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
